/**
 * <share-button> is a web component which wraps
 * the native share mechanism of your system.
 * It can share text and URLs.
 * Note: Only defined if the system supports this mechanism.
 *
 * Copyright © 2022 Tony Spegel
 */

const template: HTMLTemplateElement = document.createElement('template');
const canShare: boolean = 'canShare' in navigator;

template.innerHTML = `
    <style>
        :host {
            --base-gap: 8px;
            --base-radius: 8px;

            --purple-300: #d8b4fe;
            --purple-400: #c084fc;
            --purple-500: #a855f7;
            --purple-950: #2f0050;

            --bg-color: var(--purple-300);
            --bg-interaction-color: var(--purple-400);
            --border-color: var(--purple-500);

            --bg-disabled-color: #d4d4d4;
            --border-disabled-color: #a4a4a4;

            --text-color-1: var(--purple-950);

            display: inline-block;
        }

        :host([hidden]) {
            display: none;
        }

        :host([disabled]) button {
            cursor: not-allowed;
            transform: unset;
            border-color: var(--border-disabled-color);
            background-color: var(--bg-disabled-color);
        }

        ::slotted([slot="share-button-content"]) {
            display: flex;
            align-items: center;
            gap: var(--base-gap);
        }

        button {
            display: flex;
            border-radius: var(--base-radius);
            border: 1px solid var(--border-color);
            padding: var(--base-gap);

            background-color: var(--bg-color);
            color: var(--text-color-1);
            cursor: pointer;

            transition: transform 50ms ease-in-out 0s, background-color 100ms ease-in-out 0s;
        }

        button:hover,
        button:focus {
            background-color: var(--bg-interaction-color);
        }

        button:active {
            transform: scale(0.95);
        }
    </style>

    <button>
        <slot name="share-button-content"></slot>
    </button>
`;

type shareButtonAttributes =
    | 'disabled'
    | 'share-title'
    | 'share-text'
    | 'share-url';

export class ShareButton extends HTMLElement {
    private btnShare: HTMLButtonElement;
    private shareTitle = '';
    private shareText = '';
    private shareUrl = this.getShareUrl();

    constructor() {
        super();

        this.attachShadow({mode: 'open'});
        this.shadowRoot!.appendChild(template.content.cloneNode(true));

        this.btnShare =
            this.shadowRoot!.querySelector<HTMLButtonElement>('button')!;

        this._handleShare = this._handleShare.bind(this);
    }
    /**
     * Invoked when the component is added to the DOM for the first time.
     *
     */
    public connectedCallback(): void {
        /**
         * Check if author-set, global attributes are not set.
         * Could be attributes such as role or tabindex.
         */
        if (!this.hasAttribute('tabindex') && !this.hasAttribute('disabled')) {
            this.btnShare.setAttribute('tabindex', '0');
        }
        /**
         * Component is not disabled, add EventListener
         */
        if (!this.hasAttribute('disabled')) {
            this.btnShare.addEventListener('click', this._handleShare);
        }
        /**
         * If component is disabled make it non-focusable
         */
        if (this.hasAttribute('disabled')) {
            this.btnShare.removeEventListener('click', this._handleShare);
            this.btnShare.setAttribute('tabindex', '-1');
            // this.btnShare.setAttribute('disabled', '');
        }
    }
    /**
     * Invoked when the component is disconnected from the DOM
     */
    public disconnectedCallback() {
        this.btnShare.removeEventListener('click', this._handleShare);
    }
    /**
     * Observe the attributes to set corresponding properties
     */
    static get observedAttributes(): shareButtonAttributes[] {
        return ['share-title', 'share-text', 'share-url', 'disabled'];
    }
    /**
     * Invoked when an attribute is added, removed or changed on this component.
     * This is only executed when calling observedAttributes().
     */
    public attributeChangedCallback(
        attribute: shareButtonAttributes,
        oldValue: string,
        newValue: string,
    ): void {
        if (oldValue !== newValue) {
            switch (attribute) {
                case 'share-title':
                    console.log(newValue);

                    this.shareTitle = newValue;
                    break;
                case 'share-text':
                    console.log(newValue);

                    this.shareText = newValue;
                    break;
                case 'share-url':
                    console.log(newValue);
                    
                    this.shareUrl = newValue;
                    break;
                case 'disabled':
                    console.log(this);
                    console.log(this.hasAttribute('disabled'));
                    
                    
                    if (this.hasAttribute('disabled') === false) {
                        this.btnShare.addEventListener(
                            'click',
                            this._handleShare,
                        );
                    } else {
                        console.log('remove');
                        
                        this.btnShare.removeEventListener(
                            'click',
                            this._handleShare,
                        );
                    }
                    break;
                default:
                    break;
            }
        }
    }
    get disabled() {
        if (this.hasAttribute('disabled')) {
            return this.getAttribute('disabled');
        }
        return '';
    }
    set disabled(value) {
        // Cast string value to boolean
        const isDisabled = Boolean(value);
        if (isDisabled) {
            this.setAttribute('disabled', '');
        }
        else {
            this.removeAttribute('disabled');
        }
    }
    /**
     * If a canonical link is present return this as the URL which gets shared.
     * Otherwise return document.location.href
     */
    private getShareUrl(): string {
        const canonicalElement: HTMLLinkElement | null =
            document.querySelector<HTMLLinkElement>('link[rel=canonical]');
        // Default to location.href if canoncial url isn't present
        let url = document.location.href;

        // Use the canonical link element if present
        if (canonicalElement !== null) {
            url = canonicalElement.href;
        }

        return url;
    }
    /**
     * Call to navigator.share with optional param.
     * In this component only 'text', 'title' & 'url'
     * are used from the ShareData interface.
     */
    private async shareContent(data?: ShareData): Promise<void> {
        console.log('share');
        
        if (canShare) {
            try {
                await navigator.share(data);
            } catch (errorMessage) {
                console.error(errorMessage);
            }
        }
    }
    /**
     * Handler for shareContent
     */
    private async _handleShare(): Promise<void> {
        await this.shareContent({
            title: this.shareTitle,
            text: this.shareText,
            url: this.shareUrl,
        });
    }
}


    customElements.define('share-button', ShareButton);


declare global {
    interface HTMLElementTagNameMap {
        'share-button': ShareButton;
    }
}
